import React from "react";
import { FormattedMessage, type MessageDescriptor, useIntl } from "react-intl";

import { DeliveryDestination, trackingLink, UPS_TRACK_LOCALE_MAP } from "~/actions/deliveries";
import { isChildrenShortCourse } from "~/common/courses";
import { getLastCorrection } from "~/common/patient";
import { FormatDate } from "~/components/common/FormatDate";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import { Table, TableCell, TableEmpty, TableHead, TableRow } from "~/components/ui/table";
import type { TDelivery } from "~/types/delivery";
import type { TPatient } from "~/types/patient";

export function PatientDeliveries({ patient, deliveries }: { patient: TPatient; deliveries: TDelivery[] }) {
  const alignerDeliveries = deliveries.filter((delivery) => delivery.destination == DeliveryDestination.CLINIC);
  const impressionShipments = deliveries.filter((delivery) => delivery.destination == DeliveryDestination.LABORATORY);
  const courierPrivilege = getLastCorrection(patient).order_options.courier_deliveries_privilege;

  return (
    <>
      <DeliveryTable patient={patient} deliveries={alignerDeliveries} header="pat.block.deliveries.shipments.aligners" empty="pat.block.deliveries.not_found" showShipmentDateColumn={false} />

      {courierPrivilege ? <DeliveryTable patient={patient} deliveries={impressionShipments} header="pat.block.deliveries.deliveries.impressions" empty="pat.block.shipments.not_found" /> : null}
    </>
  );
}

function DeliveryTable({
  patient,
  deliveries,
  header,
  empty,
  showShipmentDateColumn = true,
}: {
  patient: TPatient;
  deliveries: TDelivery[];
  header: MessageDescriptor["id"];
  empty: MessageDescriptor["id"];
  showShipmentDateColumn?: boolean;
}) {
  const intl = useIntl();

  const upsLocale = UPS_TRACK_LOCALE_MAP[intl.locale];
  const isChildrenShort = isChildrenShortCourse(patient.course.course_id);

  return (
    <Portlet>
      <PortletTitle as="h2" iconClassName="icon-envelope">
        <FormattedMessage id={header} />
      </PortletTitle>

      <div className="portlet-body">
        <div id="doctor_pacients_table_wrapper" className="dataTables_wrapper no-footer">
          <Table>
            <thead>
              <TableRow>
                <TableHead style={{ width: 150 }}>
                  <FormattedMessage id="pat.block.deliveries.waybills" />
                </TableHead>

                {showShipmentDateColumn ? (
                  <TableHead style={{ width: 79 }}>
                    <FormattedMessage id="pat.block.deliveries.shipment.date" />
                  </TableHead>
                ) : null}

                <TableHead style={{ width: 79 }}>
                  <FormattedMessage id={isChildrenShort ? "pat.block.payment.payments" : "pat.block.right.correction"} />
                </TableHead>
              </TableRow>
            </thead>

            <tbody>
              {deliveries.length == 0 ? (
                <TableEmpty columns={showShipmentDateColumn ? 3 : 2}>
                  <FormattedMessage id={empty} />
                </TableEmpty>
              ) : (
                deliveries.map((delivery) => (
                  <TableRow key={delivery.track_number}>
                    <TableCell>
                      <a href={trackingLink(delivery.track_number, delivery.type, upsLocale)} rel="noopener noreferrer" target="_blank">
                        {delivery.track_number}
                      </a>
                    </TableCell>

                    {showShipmentDateColumn ? (
                      <TableCell>
                        <FormatDate value={delivery.pickup_date} date />
                      </TableCell>
                    ) : null}

                    <TableCell>
                      {delivery.correction_number > 0 ? (
                        <>
                          <FormattedMessage id={isChildrenShort ? "pat.block.right.additional_course" : "pat.block.right.correction"} /> {delivery.correction_number}
                        </>
                      ) : (
                        <FormattedMessage id="pat.block.right.general.treatment" />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </Portlet>
  );
}
